import { api } from '@/lib/api/client'
import { fetchAll } from '@/lib/api/paged'

/* ── Teams ─────────────────────────────────────────────────────────────── */

export interface Team {
  id: string
  name: string
  divisionId: string | null
  division?: { id: string; name: string } | null
  leadUserId?: string | null
  members?: { id: string; name: string; color: string | null }[]
}

export async function fetchTeams(): Promise<Team[]> {
  const { data } = await api.get<{ data: Team[] }>('/teams')

  return data.data
}

export async function createTeam(input: { name: string; divisionId: string }): Promise<Team> {
  const { data } = await api.post<{ data: Team }>('/teams', input)

  return data.data
}

export async function updateTeam(
  id: string,
  changes: { name?: string; leadUserId?: string | null },
): Promise<Team> {
  const { data } = await api.patch<{ data: Team }>(`/teams/${id}`, changes)

  return data.data
}

export async function deleteTeam(id: string): Promise<void> {
  await api.delete(`/teams/${id}`)
}

export async function addTeamMember(teamId: string, userId: string): Promise<void> {
  await api.post(`/teams/${teamId}/members/${userId}`, {})
}

export async function removeTeamMember(teamId: string, userId: string): Promise<void> {
  await api.delete(`/teams/${teamId}/members/${userId}`)
}

/* ── Repositories ──────────────────────────────────────────────────────── */

export interface Repository {
  id: string
  provider: string
  externalId: string
  fullName: string
  defaultBranch: string | null
  webhookSecret?: string | null
  projectIds?: string[]
  projects?: { id: string; key: string; name: string }[]
}

export async function fetchRepositories(): Promise<Repository[]> {
  return fetchAll<Repository>('/repositories')
}

export async function createRepository(input: {
  provider: string
  externalId: string
  fullName: string
  defaultBranch?: string
  projectIds: string[]
}): Promise<Repository> {
  const { data } = await api.post<{ data: Repository }>('/repositories', input)

  return data.data
}

export async function updateRepository(
  id: string,
  changes: { fullName?: string; defaultBranch?: string; projectIds?: string[] },
): Promise<Repository> {
  const { data } = await api.patch<{ data: Repository }>(`/repositories/${id}`, changes)

  return data.data
}

export async function deleteRepository(id: string): Promise<void> {
  await api.delete(`/repositories/${id}`)
}

/** Returns the new secret once. It is not readable again afterwards. */
export async function rotateRepositorySecret(id: string): Promise<{ secret: string }> {
  const { data } = await api.post<{ data: { secret: string } }>(`/repositories/${id}/rotate-secret`, {})

  return data.data
}

/* ── Canned responses ──────────────────────────────────────────────────── */

export interface CannedResponse {
  id: string
  title: string
  body: string
  locale: string
}

export async function fetchCannedResponses(): Promise<CannedResponse[]> {
  const { data } = await api.get<{ data: CannedResponse[] }>('/canned-responses')

  return data.data
}

export async function createCannedResponse(input: {
  title: string
  body: string
  locale?: string
}): Promise<CannedResponse> {
  const { data } = await api.post<{ data: CannedResponse }>('/canned-responses', input)

  return data.data
}

export async function updateCannedResponse(
  id: string,
  changes: { title?: string; body?: string; locale?: string },
): Promise<CannedResponse> {
  const { data } = await api.patch<{ data: CannedResponse }>(`/canned-responses/${id}`, changes)

  return data.data
}

export async function deleteCannedResponse(id: string): Promise<void> {
  await api.delete(`/canned-responses/${id}`)
}

/** What changed between two refs, assembled from the work items the commits name. */
export async function fetchReleaseNotes(
  repositoryId: string,
  range: { from: string; to: string },
): Promise<{ items: { key: string; title: string }[]; from: string; to: string }> {
  const { data } = await api.get<{
    data: { items: { key: string; title: string }[]; from: string; to: string }
  }>(`/repositories/${repositoryId}/release-notes`, { params: range })

  return data.data
}

export interface DivisionDefaults {
  divisionId: string
  divisionKey: string
  divisionName: string
  deliveryWorkflowId: string | null
  engagementWorkflowId: string | null
}

export async function fetchWorkflowDefaults(): Promise<DivisionDefaults[]> {
  const { data } = await api.get<{ data: DivisionDefaults[] }>('/workflows/defaults')

  return data.data
}

/** What a new project in this division starts with. */
export async function setWorkflowDefaults(
  divisionId: string,
  input: { deliveryWorkflowId?: string | null; engagementWorkflowId?: string | null },
): Promise<DivisionDefaults[]> {
  const { data } = await api.put<{ data: DivisionDefaults[] }>(
    `/workflows/defaults/${divisionId}`,
    input,
  )

  return data.data
}
